import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { AddFormatDialog } from "@/components/pricing/AddFormatDialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const FIELDS = [["label", "Label", "text"], ["mounting", "Presentation (Loose / Mesh-mounted)", "text"],
  ["tw", "Tile width (in)", "num"], ["tl", "Tile length (in)", "num"], ["mw", "Mesh sheet width (in)", "num", "mesh"], ["ml", "Mesh sheet length (in)", "num", "mesh"],
  ["pieces_per_sheet", "Pieces per sheet", "int", "mesh"], ["sheets_per_box", "Sheets per box", "int", "mesh"], ["pieces_per_box", "Pieces per box", "int", "loose"],
  ["sqft_per_box", "Sq ft per box", "num"], ["boxes_per_pallet", "Boxes per pallet", "int"], ["specs", "Other specifications", "text"]];
const seed = (r) => ({ ...r, tw: r.tile_dims_in?.[0], tl: r.tile_dims_in?.[1], mw: r.mesh_dims_in?.[0], ml: r.mesh_dims_in?.[1] });

// ID-079: edit an existing format's specifications (SKU prefix and code are permanent; history keeps its snapshots)
const EditFormatDialog = ({ row, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [note, setNote] = useState("");
  const [conflict, setConflict] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) { const s = seed(row); setF(Object.fromEntries(FIELDS.map(([k]) => [k, s[k] ?? ""]))); setNote(""); setConflict(null); } }, [row]);
  if (!row) return null;
  const mesh = f.mounting === "Mesh-mounted";
  const shown = FIELDS.filter(([, , , m]) => !m || (m === "mesh") === mesh);
  const bad = shown.filter(([k, , t]) => t !== "text" && f[k] !== "" && f[k] != null && !(Number(f[k]) > 0 && (t !== "int" || Number.isInteger(Number(f[k])))));
  const save = async (confirm) => {
    const body = { note: note || "Admin Pricing — Edit format", confirm_conflicts: confirm };
    const s = seed(row);
    shown.forEach(([k, , t]) => { const v = f[k]; if (["tw", "tl", "mw", "ml"].includes(k) || v === "" || v == null || String(v) === String(s[k] ?? "")) return; body[k] = t === "text" ? v : Number(v); });
    if (f.tw && f.tl && `${f.tw}x${f.tl}` !== `${s.tw}x${s.tl}`) body.tile_size = [Number(f.tw), Number(f.tl)];
    if (mesh && f.mw && f.ml && `${f.mw}x${f.ml}` !== `${s.mw}x${s.ml}`) body.mesh_sheet = [Number(f.mw), Number(f.ml)];
    setBusy(true);
    try { await api.patch(`/admin/pricing/formats/${row.code}`, body); toast.success(`${row.label} updated`); onSaved(); onClose(); }
    catch (e) { const d = e.response?.data?.detail; if (d?.code === "FORMAT_SPEC_CONFLICT") setConflict(d.message); else toast.error(formatApiErrorDetail(d)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg" data-testid="edit-format-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Edit format · {row.label}</DialogTitle>
          <DialogDescription className="text-xs text-warmgrey">Code {row.code} and SKU prefix {row.sku_prefix} are permanent. Orders, receipts and audit history keep their recorded values; no stock moves. Empty fields stay empty.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {shown.map(([k, l, t]) => (
            <label key={k} className="text-xs text-warmgrey">{l}
              {k === "mounting"
                ? <select value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-9 w-full rounded-md border border-border bg-white px-2 text-sm" data-testid="edit-format-mounting"><option>Loose</option><option>Mesh-mounted</option></select>
                : <Input type={t === "text" ? "text" : "number"} step={t === "int" ? "1" : "0.01"} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-9 bg-white" data-testid={`edit-format-${k}`} />}
            </label>
          ))}
        </div>
        {mesh && f.pieces_per_sheet && f.sheets_per_box ? <p className="text-xs text-charcoal" data-testid="edit-format-pieces-total">Total pieces per box (calculated): {Number(f.pieces_per_sheet) * Number(f.sheets_per_box)}</p> : null}
        {row.is_mosaic_collection && <p className="text-xs text-warmgrey">Also editable with prices and costs under Products → Mosaic Collections → {row.label}.</p>}
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for the change (audit)" className="h-9 bg-white" data-testid="edit-format-note" />
        {bad.length > 0 && <p className="text-xs text-status-sold" data-testid="edit-format-invalid">{bad.map(([, l]) => l).join(", ")}: enter a value above 0{bad.some(([, , t]) => t === "int") ? " (whole numbers for counts)" : ""}</p>}
        {conflict && <p className="rounded-md border border-terracotta/40 bg-terracotta/5 p-2 text-xs text-charcoal" data-testid="edit-format-conflict">{conflict}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="edit-format-cancel">Cancel</Button>
          <Button disabled={busy || bad.length > 0} onClick={() => save(!!conflict)} className="bg-terracotta text-white hover:bg-charcoal" data-testid="edit-format-save">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}{conflict ? "Confirm and save" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ID-059: registry of product formats feeding the shared pricing engine
export function ProductFormats({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/pricing/formats").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChanged?.(); };
  const toggle = async (r, active) => {
    try { await api.patch(`/admin/pricing/formats/${r.code}`, { active, note: "Admin Pricing — activation" }); toast.success(`${r.label} ${active ? "activated" : "deactivated"}`); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mb-6 rounded-md border border-border bg-white p-4" data-testid="product-formats">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Product formats</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-terracotta text-white hover:bg-charcoal" data-testid="add-product-format-btn"><Plus className="mr-1 h-4 w-4" /> Add Product Format</Button>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[760px] text-xs" data-testid="product-formats-table">
          <thead><tr className="border-b border-border text-left text-warmgrey">
            {["Format", "Prefix", "Type", "Tile size", "Mesh sheet", "Pcs / box", "Sq ft / box", "Boxes / pallet", "Landed cost", "Catalog variants", "Active", ""].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-b border-border/60" data-testid={`format-row-${r.code}`}>
                <td className="py-2 pr-3 font-medium text-charcoal">{r.label} <span className="text-muted-foreground">({r.code})</span></td>
                <td className="py-2 pr-3 font-mono">{r.sku_prefix}</td>
                <td className="py-2 pr-3">{r.product_type}</td>
                <td className="py-2 pr-3" data-testid={`format-tile-${r.code}`}>{r.tile_size_in || "Not set"}</td>
                <td className="py-2 pr-3">{r.mounting === "Mesh-mounted" ? r.mesh_sheet_in || "Not set" : "—"}</td>
                <td className="py-2 pr-3">{r.pieces_per_box}</td>
                <td className="py-2 pr-3">{r.sqft_per_box}</td>
                <td className="py-2 pr-3">{r.boxes_per_pallet ?? "—"}</td>
                <td className="py-2 pr-3">{r.landed_cost != null ? `$${r.landed_cost.toFixed(2)}` : "—"}</td>
                <td className="py-2 pr-3" data-testid={`format-variants-${r.code}`}>{r.variants} · {r.sellable} sellable</td>
                <td className="py-2"><Switch checked={!!r.active} onCheckedChange={(v) => toggle(r, v)} data-testid={`format-active-${r.code}`} /></td>
                <td className="py-2 pl-2"><Button size="sm" variant="outline" onClick={() => setEdit(r)} data-testid={`format-edit-${r.code}`} className="h-7 gap-1 text-xs"><Pencil className="h-3 w-3" /> Edit format</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddFormatDialog open={open} onOpenChange={setOpen} onCreated={refresh} />
      <EditFormatDialog row={edit} onClose={() => setEdit(null)} onSaved={refresh} />
    </div>
  );
}
